import { Divider, Grid } from "@material-ui/core";
import { Alert, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { Control } from "react-hook-form";
import {
  NumberField,
  SelectFieldComponent,
  TextFieldComponent
} from "../../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import GenericForm from "../../external/ensolvers-core-frontend-mui/components/Common/GenericForm";
import NotificationService from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import DateUtils from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import Holiday from "../../models/Holiday";
import WorkLog from "../../models/WorkLog";
import WorkLogService from "../../services/WorkLogService";
import HolidayUtils from "../../utils/HolidayUtils";
import NumberUtils from "../../utils/NumberUtils";
import OpsDateUtils from "../../utils/OpsDateUtils";
import RoleUtils from "../../utils/RoleUtils";
import { ButtonComponent } from "../ButtonComponent";
import WorkLogHoursWrapper from "../WorkLogHoursWrapper";
import WorklogsUtils, { MAX_HOURS_ADJUST } from "../../utils/WorklogsUtils";
import TextScrollX from "../TextScrollX";
import "../../styles/ScrollBar.css";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ListComponentWithIcon } from "../ListComponentWithIcon";
import { EnsolversOpsUser } from "../../models/EnsolversOpsUser";
import Position from "../../models/Position";
import AuthService from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { workLogCategory } from "../../utils/enums/WorkLogCategory";
import WorklogReportUtils from "../../utils/WorklogReportUtils";
import { ColorUtils } from "../../utils/ColorUtils";
import { useStyles } from "../Main/formStyles";

interface Props {
  open: boolean;
  setOpen: (newValue: boolean) => void;
  isEditMode: boolean;
  setIsEditMode: (newValue: boolean) => void;
  workLog?: WorkLog;
  position?: Position;
  setWorkLog: (updatedWorklog: WorkLog | undefined) => void;
  holidays: Holiday[];
  paramUserExternalId?: string;
  user: EnsolversOpsUser;
  setFetchUpdate: any;
}

export const WorklogModalCreation = ({
  open,
  setOpen,
  isEditMode,
  setIsEditMode,
  workLog,
  setWorkLog,
  holidays,
  paramUserExternalId,
  user,
  position,
  setFetchUpdate
}: Props) => {
  // Worklog detail fields
  const [prsPending, setPrsPending] = useState<string[]>([]);
  const [tasksDone, setTasksDone] = useState<string[]>([]);
  const [tasksInProgress, setTasksInProgress] = useState<string[]>([]);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [reportedIssues, setReportedIssues] = useState<string[]>([]);
  const [resolvedIssues, setResolvedIssues] = useState<string[]>([]);

  const customStyles = useStyles();

  useEffect(() => {
    if (workLog) {
      const worklogDetails = workLog.details;
      if (worklogDetails) {
        !!worklogDetails.prsPending && setPrsPending(worklogDetails.prsPending || []);
        !!worklogDetails.tasksDone && setTasksDone(worklogDetails.tasksDone || []);
        !!worklogDetails.tasksInProgress && setTasksInProgress(worklogDetails.tasksInProgress || []);
        !!worklogDetails.blockers && setBlockers(worklogDetails.blockers || []);
        !!worklogDetails.reportedIssues && setReportedIssues(worklogDetails.reportedIssues || []);
        !!worklogDetails.resolvedIssues && setResolvedIssues(worklogDetails.resolvedIssues || []);
      }
    }
  }, [workLog]);

  const canModifiedWorklog = (): boolean => {
    return (
      RoleUtils.isAdminOrManager() ||
      paramUserExternalId === undefined ||
      paramUserExternalId === user.externalId ||
      paramUserExternalId === AuthService.getUser()?.externalId! ||
      position !== null
    );
  };

  const handleCopyExternalDescription = (workLog: WorkLog, control: Control) => {
    setWorkLog({
      ...workLog,
      hours: control._formValues.hours,
      internalHours: control._formValues.internalHours,
      description: control._formValues.description,
      internalDescription: control._formValues.description,
    });
  };

  const resetWorklog = () => {
    setIsEditMode(false);

    setPrsPending([]);
    setTasksDone([]);
    setTasksInProgress([]);
    setBlockers([]);
    setReportedIssues([]);
    setResolvedIssues([]);
  };

  const hideWorklogModal = () => {
    setWorkLog(undefined);
    resetWorklog();
    setFetchUpdate(true);
    setOpen(false);
  };

  const handleOnSave = async (workLogCreated: WorkLog, submitContext: any) => {
    if (!workLog) {
      return;
    }

    if (NumberUtils.hasDecimals(workLogCreated?.hours) || NumberUtils.hasDecimals(workLogCreated?.internalHours)) {
      NotificationService.error("Hours cannot have decimals");
      setWorkLog(workLogCreated);
      return;
    }

    if (workLogCreated.adjustmentHours != null && !WorklogReportUtils.adjustmentHoursIsValid(workLogCreated, position)) {
      setWorkLog(workLogCreated);
      return;
    }

    if (workLogCreated.bonusHours != null && !WorklogReportUtils.bonusHoursIsValid(workLogCreated, position)) {
      setWorkLog(workLogCreated);
      return;
    }

    workLogCreated.details = {
      prsPending,
      tasksDone,
      tasksInProgress,
      blockers,
      reportedIssues,
      resolvedIssues,
    };

    if (isEditMode) {
      // Update
      try {
        await WorkLogService.update(workLogCreated);
        NotificationService.info("Worklog updated!");
        hideWorklogModal();
        
      } catch (error: any) {
        NotificationService.error(error?.response?.data?.message || "Error trying to update.");
        return;
      }
    } else {
      // Create
      try {
        workLogCreated.shortDay = OpsDateUtils.toUTCWithSetHours(workLogCreated.shortDay);
        await WorkLogService.create(workLogCreated);
        NotificationService.info("Worklog created!");
        hideWorklogModal();
      } catch (error: any) {
        let errorMessage = "WorkLog for that day already exists!";

        if (error?.response?.data?.message) {
          errorMessage = error?.response?.data?.message;
        }

        NotificationService.error(errorMessage);
        return;
      }


      // "When Pressing "Enter" you have “quick fill” feature
      if (submitContext?.trigger === "ENTER_KEY_PRESSED") {
        const lastDate = workLog.shortDay.getDate();
        workLog.shortDay.setDate(workLog.shortDay.getDate() + 1);

        while (OpsDateUtils.isWeekend(workLog.shortDay) || HolidayUtils.isHoliday(workLog.shortDay, holidays)) {
          workLog.shortDay.setDate(workLog.shortDay.getDate() + 1);
        }

        // If the next workLog is on the next month. The modal closes.
        if (lastDate < workLog.shortDay.getDate()) {
          setWorkLog(workLog);
          setOpen(true);
        }
      }
    }
  };

  return (
    <GenericForm<WorkLog>
      headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
      buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
      additionalButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
      dialogStyles={customStyles.root}      
      dialog={true}
      open={open}
      instance={workLog}
      title={(isEditMode ? "Edit " : "Create ") + DateUtils.formatAsDDDMMMYYY(workLog?.shortDay!) + " Worklog "}
      onSave={handleOnSave}
      onCancel={resetWorklog}
      validateBeforeClosing
      disableBackdropClick
      renderContent={(register, errors, control) => (
        <div className="scrollableY">
          {(workLog?.isWeekend || workLog?.isHoliday) && (
            <Alert severity="warning" sx={{ ml: "0.5em", mb: "1em" }}>
              The day you selected is a <b>{workLog?.isWeekend ? 'WEEKEND' : 'HOLIDAY'}</b> day. If you report hours here, they will be considered extra hours.
            </Alert>
          )}

          <TextFieldComponent propertyName={"assignationName"} label="Assignation" required={true} readOnly />

          {/* External hours */}
          <WorkLogHoursWrapper
            HoursElement={
              <NumberField
                propertyName={"hours"}
                label="Hours"
                required={true}
                inputType="number"
                readOnly={!canModifiedWorklog()}
                enableEditInputProps={true}
                min={0}
                max={24}
              />
            }
            DescriptionElement={
              <TextFieldComponent
                propertyName={"description"}
                label="Description"
                required={true}
                internalProps={{ autoFocus: true }}
                readOnly={!canModifiedWorklog()}
              />
            }
          />

          {/* Internal hours */}
          <WorkLogHoursWrapper
            HoursElement={
              <NumberField
                propertyName={"internalHours"}
                label="Internal Hours"
                required={false}
                inputType="number"
                readOnly={!canModifiedWorklog()}
                enableEditInputProps={true}
                min={0}
                max={24}
              />
            }
            DescriptionElement={
              <TextFieldComponent
                propertyName={"internalDescription"}
                label="Internal Description"
                required={false}
                readOnly={!canModifiedWorklog()}
              />
            }
          />

          <ButtonComponent onClick={() => handleCopyExternalDescription(workLog!, control)} text={"Copy external description"} />

          {RoleUtils.isAdminOrManager() && (
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <NumberField
                  propertyName={"adjustmentHours"}
                  label="Adjustment"
                  required={false}
                  inputType="number"
                  pattern={/^-?[0-9]+$/}
                  customHelperText="Adjustment should be an integer."
                  enableEditInputProps={true}
                  max={24}
                />
              </Grid>
              {!!position && (position.bonus == 0 || position.bonus == null) && (
                <Grid item xs={3}>
                  <NumberField
                    propertyName={"bonusHours"}
                    label="Bonus"
                    required={false}
                    inputType="number"
                    enableEditInputProps={true}
                    min={0}
                    pattern={/^[0-9]+$/}
                    customHelperText="Bonus should be a number greater than or equal to zero."
                  />
                </Grid>
              )}
            </Grid>
          )}

          <SelectFieldComponent
            control={control}
            register={register}
            defaultValue={"REGULAR"}
            errors={errors}
            propertyName={"category"}
            label={"Worklog Type"}
            required={true}
            options={Object.entries(workLogCategory).map(([value, label]) => ({
              label,
              value,
            }))}
          />
          {/* Worklog details fields */}
          <ListComponentWithIcon
            title={"PRS Pending"}
            name="prs-pending"
            items={prsPending}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setPrsPending([...prsPending, newItem])}
            onDelete={(itemIndex) => setPrsPending(prsPending.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(prsPending.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Tasks Done"}
            name="tasks-done"
            items={tasksDone}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setTasksDone([...tasksDone, newItem])}
            onDelete={(itemIndex) => setTasksDone(tasksDone.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(tasksDone.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Tasks In Progress"}
            name="tasks-in-progress"
            items={tasksInProgress}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setTasksInProgress([...tasksInProgress, newItem])}
            onDelete={(itemIndex) => setTasksInProgress(tasksInProgress.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(tasksInProgress.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Blockers"}
            name="blockers"
            items={blockers}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setBlockers([...blockers, newItem])}
            onDelete={(itemIndex) => setBlockers(blockers.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(blockers.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Reported issues"}
            name="reported-issues"
            items={reportedIssues || []}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setReportedIssues([...reportedIssues, newItem])}
            onDelete={(itemIndex) => setReportedIssues(reportedIssues.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(reportedIssues.toString());
            }}
          />
          <ListComponentWithIcon
            title={"Resolved issues"}
            name="resolved-issues"
            items={resolvedIssues || []}
            renderItem={(item, index) => <TextScrollX>• {item}</TextScrollX>}
            onAdd={(newItem) => setResolvedIssues([...resolvedIssues, newItem])}
            onDelete={(itemIndex) => setResolvedIssues(resolvedIssues.filter((item, index) => index !== itemIndex))}
            icon={<ContentCopyIcon fontSize="small" />}
            handleOnClick={() => {
              navigator.clipboard.writeText(resolvedIssues.toString());
            }}
          />
        </div>
      )}
      hideCancel
      additionalButtons={[
        {
          label: "Cancel",
          onAction: hideWorklogModal,
        },
      ]}
    />
  );
};
